// src/pages/finance-portal/inventory/ProductDetail.js
//
// Product detail.
// Tabs: Overview, Stock by Location, Cost Layers, Movement History.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
    Divider, Grid, IconButton, Paper, Skeleton, Stack,
    Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import axiosInstance from '../../../utils/axiosInstance';
import {
    FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK,
    formatCurrency, formatQty, formatDateTime,
    PRODUCT_STATUS_COLORS, PRODUCT_TYPE_COLORS, statusChip,
} from './components/inventoryFormatters';

export default function ProductDetail() {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState(0);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/inventory/products/${productId}`);
            if (res.data?.success) setProduct(res.data.data);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [productId]);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <Box sx={{ p: 3, fontFamily: FONT_FAMILY }}>
                <Skeleton height={80} />
                <Skeleton height={40} sx={{ mt: 2 }} />
                <Skeleton variant="rectangular" height={260} sx={{ mt: 2 }} />
            </Box>
        );
    }
    if (error || !product) {
        return (
            <Box sx={{ p: 3, fontFamily: FONT_FAMILY }}>
                <Alert severity="error">{error || 'Product not found'}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={() => navigate('/finance-portal/inventory/products')}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' }}>
                                {product.sku}
                            </Typography>
                            <Chip size="small" label={product.product_type?.replace('_', ' ')}
                                style={statusChip(product.product_type, PRODUCT_TYPE_COLORS)}
                                sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} />
                            <Chip size="small" label={product.status}
                                style={statusChip(product.status, PRODUCT_STATUS_COLORS)}
                                sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} />
                        </Stack>
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.05rem', color: '#334155' }}>
                            {product.name}
                        </Typography>
                        {product.category_name && (
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem', color: '#64748b' }}>
                                {product.category_name}
                            </Typography>
                        )}
                    </Box>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<EditIcon />}
                        onClick={() => navigate(`/finance-portal/inventory/products/${productId}/edit`)}
                        sx={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>
                        Edit
                    </Button>
                </Stack>
            </Box>

            {/* Summary KPIs */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.78rem', color: '#64748b' }}>Total On Hand</Typography>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.35rem', fontWeight: 700, color: '#0f172a' }}>
                                {formatQty(product.total_on_hand || 0)} {product.unit_of_measure}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.78rem', color: '#64748b' }}>Total Value</Typography>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.35rem', fontWeight: 700, color: '#0f172a' }}>
                                {formatCurrency(product.total_value || 0, product.currency)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.78rem', color: '#64748b' }}>Average Cost</Typography>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.35rem', fontWeight: 700, color: '#0f172a' }}>
                                {formatCurrency(product.average_cost || 0, product.currency)}
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.72rem', color: '#64748b' }}>
                                {product.valuation_method?.replace('_', ' ')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.78rem', color: '#64748b' }}>Reorder Point</Typography>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.35rem', fontWeight: 700, color: '#0f172a' }}>
                                {formatQty(product.reorder_point || 0)}
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.72rem', color: '#64748b' }}>
                                Lead time {product.lead_time_days || 0} days
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{
                    borderBottom: '1px solid #e2e8f0',
                    '& .MuiTab-root': { fontFamily: FONT_FAMILY, textTransform: 'none', fontWeight: 600 },
                }}>
                    <Tab label="Overview" />
                    <Tab label={`Stock by Location (${product.stock_by_location?.length || 0})`} />
                    <Tab label={`Cost Layers (${product.open_cost_layers?.length || 0})`} />
                    <Tab label={`Movements (${product.recent_movements?.length || 0})`} />
                </Tabs>

                <Box sx={{ p: 3 }}>
                    {tab === 0 && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, mb: 1.5 }}>Details</Typography>
                                <Stack spacing={1}>
                                    <KV label="Description" value={product.description || '—'} />
                                    <KV label="Barcode" value={product.barcode || '—'} />
                                    <KV label="MPN" value={product.mpn || '—'} />
                                    <KV label="Currency" value={product.currency} />
                                    <KV label="List Price" value={formatCurrency(product.unit_price, product.currency)} />
                                    <KV label="Unit Cost" value={formatCurrency(product.unit_cost, product.currency)} />
                                    <KV label="Preferred Vendor" value={product.preferred_vendor_name || '—'} />
                                    <KV label="UoM" value={product.unit_of_measure} />
                                    <KV label="Weight" value={product.weight ? `${product.weight} ${product.weight_unit || ''}` : '—'} />
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, mb: 1.5 }}>Tracking &amp; Flags</Typography>
                                <Stack spacing={1}>
                                    <KV label="Track Lots" value={product.track_lots ? 'Yes' : 'No'} />
                                    <KV label="Track Serials" value={product.track_serials ? 'Yes' : 'No'} />
                                    <KV label="Track Expiry" value={product.track_expiry ? 'Yes' : 'No'} />
                                    <KV label="Sellable" value={product.is_sellable ? 'Yes' : 'No'} />
                                    <KV label="Purchasable" value={product.is_purchasable ? 'Yes' : 'No'} />
                                    <KV label="Dropship Only" value={product.is_dropship_only ? 'Yes' : 'No'} />
                                    <KV label="Backorder" value={product.allow_backorder ? 'Allowed' : 'Blocked'} />
                                    <KV label="Channels" value={(product.channels || []).join(', ') || '—'} />
                                </Stack>
                            </Grid>
                        </Grid>
                    )}

                    {tab === 1 && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Location</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">On Hand</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Reserved</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Available</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">On Order</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Reorder Pt</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(product.stock_by_location || []).map((l) => (
                                    <TableRow key={l.id}>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }}>
                                            {l.location_code} <span style={{ color: '#64748b' }}>{l.location_name}</span>
                                        </TableCell>
                                        <TableCell sx={{
                                            fontFamily: FONT_FAMILY, fontWeight: l.needs_reorder ? 700 : 500,
                                            color: l.needs_reorder ? '#dc2626' : '#0f172a',
                                        }} align="right">
                                            {formatQty(l.quantity_on_hand)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatQty(l.quantity_reserved)}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatQty(l.quantity_available)}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatQty(l.quantity_on_order)}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatQty(l.reorder_point || 0)}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">{formatCurrency(l.value_on_hand)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}

                    {tab === 2 && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Received</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Source</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Original Qty</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Remaining</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Unit Cost</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Open Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(product.open_cost_layers || []).map((cl) => (
                                    <TableRow key={cl.id}>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }}>{formatDateTime(cl.received_at)}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }}>{cl.source?.replace('_', ' ')}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatQty(cl.original_qty)}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">{formatQty(cl.remaining_qty)}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatCurrency(cl.unit_cost)}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">
                                            {formatCurrency(cl.total_cost_remaining || (cl.remaining_qty * cl.unit_cost))}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}

                    {tab === 3 && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>When</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Type</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Location</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Qty</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Unit Cost</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Total</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Memo</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(product.recent_movements || []).map((m) => (
                                    <TableRow key={m.id}>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.8rem' }}>
                                            {formatDateTime(m.occurred_at)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }}>
                                            <Chip size="small" label={m.movement_type?.replace('_', ' ')}
                                                sx={{
                                                    fontFamily: FONT_FAMILY, fontWeight: 600,
                                                    bgcolor: m.signed_quantity >= 0 ? '#dcfce7' : '#fee2e2',
                                                    color: m.signed_quantity >= 0 ? '#166534' : '#991b1b',
                                                }} />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.8rem' }}>
                                            {m.location_code}
                                        </TableCell>
                                        <TableCell sx={{
                                            fontFamily: FONT_FAMILY,
                                            color: m.signed_quantity >= 0 ? '#166534' : '#991b1b',
                                            fontWeight: 600,
                                        }} align="right">
                                            {m.signed_quantity >= 0 ? '+' : ''}{formatQty(m.signed_quantity)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatCurrency(m.unit_cost)}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">{formatCurrency(m.total_cost)}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.8rem', color: '#64748b' }}>{m.memo || '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}

function KV({ label, value }) {
    return (
        <Stack direction="row" spacing={2}>
            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem', color: '#64748b', minWidth: 130 }}>
                {label}
            </Typography>
            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.88rem', color: '#0f172a' }}>
                {value}
            </Typography>
        </Stack>
    );
}
